import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Input,
  Tag,
  Typography,
  Tooltip
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined, SearchOutlined, CopyOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import CouponModal from './CouponModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import dayjs from 'dayjs';

const { Text } = Typography;

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Coupon {
  coupon_id: number;
  coupon_code: string;
  coupon_amount: number;
  coupon_max_count: number;
  coupon_expire_date: string;
  coupon_description: string;
  coupon_status: number;
  coupon_created_on: string;
}

const CouponScreen: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Coupon | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [keywordSearch, setKeywordSearch] = useState('');

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCoupons = async () => {
    try {
      const data = await apiRequest<Coupon[]>({
        method: 'get',
        url: 'get-coupons?keyword=' + keywordSearch,
      });
      setCoupons(data || []);
    } catch (error) {
      showError('Failed to fetch coupons');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [keywordSearch]);

  const handleEditClick = (record: Coupon) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCoupons();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (coupon_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-coupon/${coupon_id}`,
      });
      showSuccess('Coupon deleted');
      fetchCoupons();
    } catch {
      showError('Failed to delete Coupon');
    }
  };

  const handleStatusToggle = async (couponId: number) => {
    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-coupon/${couponId}`,
      });
      showSuccess('Coupon status updated');
      fetchCoupons();
    } catch (error) {
      showError('Failed to update status');
    }
  };

  const handleTableChange: TableProps<Coupon>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Coupon> = [
    {
      title: 'Code',
      dataIndex: 'coupon_code',
      key: 'coupon_code',
      sorter: (a, b) => a.coupon_code.localeCompare(b.coupon_code),
      sortOrder: sortedInfo.columnKey === 'coupon_code' ? sortedInfo.order : null,
      render: (text) => (
        <Space>
          <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>{text}</Tag>
          <Tooltip title="Copy Code">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined style={{ color: '#1890ff' }} />}
              onClick={() => {
                navigator.clipboard.writeText(text);
                message.success('Code copied!');
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'coupon_amount',
      key: 'coupon_amount',
      sorter: (a, b) => a.coupon_amount - b.coupon_amount,
      sortOrder: sortedInfo.columnKey === 'coupon_amount' ? sortedInfo.order : null,
      render: (amount) => `₹${amount}`,
    },
    {
      title: 'Usage Limit',
      dataIndex: 'coupon_max_count',
      key: 'coupon_max_count',
      align: 'center',
      render: (count) => <Tag color="cyan">{count}</Tag>,
    },
    {
      title: 'Expiry Date',
      dataIndex: 'coupon_expire_date',
      key: 'coupon_expire_date',
      render: (date) => date ? dayjs(date).format('DD-MMM-YYYY') : 'No Expiry',
    },
    {
      title: 'Status',
      dataIndex: 'coupon_status',
      key: 'coupon_status',
      render: (status, record) => (
        <Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          checked={status === 1}
          onChange={() => handleStatusToggle(record.coupon_id)}
          disabled={currentModule?.right_edit !== 'Y'}
        />
      ),
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 120,
      align: 'center',
      render: (_: any, record: Coupon) => (
        <Space size="small">
          {currentModule?.right_edit === 'Y' && (
            <Button size="small" onClick={() => handleEditClick(record)}>
              <EditOutlined />
            </Button>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this coupon?"
              onConfirm={() => handleDelete(record.coupon_id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  const handleKeywordSearch = (value: string) => {
    setKeywordSearch(value);
  };
  return (
    <Flex vertical style={{ height: "calc(100vh - 145px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
          padding: '0 16px',
          paddingTop: 8
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Coupons</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small" align="center">
          <Input.Search
            placeholder="Search coupon code"
            allowClear
            onSearch={handleKeywordSearch}
            style={{ width: 250 }}
            value={keywordSearch}
            onChange={e => setKeywordSearch(e.target.value)}
            enterButton={<SearchOutlined />}
            size="middle"
          />
          {currentModule?.right_add === 'Y' && (
            <Button type="primary" onClick={showCreateModal}>
              Add Coupon
            </Button>
          )}
        </Flex>
      </Space>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          margin: '0 16px 16px 16px',
          overflow: 'hidden'
        }}
      >
        <Table<Coupon>
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="coupon_id"
          dataSource={coupons.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 'calc(100vh - 400px)' }}
          locale={{ emptyText: 'No coupons found' }}
          style={{ flex: 1 }}
          footer={() => (
            <Flex justify="space-between" align="center">
              <Text type="secondary">
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, coupons.length)} of{' '}
                {coupons.length}
              </Text>
              <Pagination
                {...pagination}
                total={coupons.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <CouponModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </Flex>
  );
};

export default CouponScreen;